import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { errMsg, limitInfo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GoogleButton, OrDivider } from "@/components/GoogleButton";
import { initTheme } from "@/lib/theme";
import { toast } from "@/components/ui/sonner";
import { Loader2, ArrowRight, ScanLine, CheckCircle2, Sparkles } from "lucide-react";

const PHASES = ["Connecting to site...", "Reading markup & styles", "Extracting palette & type", "Distilling brand voice"];
const CACHE_KEY = "hdg_pending_brand";
const CACHE_TTL_MS = 60 * 60 * 1000;

export default function OnboardingEntry() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, register } = useAuth();
  const [phase, setPhase] = useState("input"); // input | analyzing | reveal
  const [url, setUrl] = useState("");
  const [brand, setBrand] = useState(null);
  const [manualFallback, setManualFallback] = useState(false);
  const [scanStep, setScanStep] = useState(0);
  const [form, setForm] = useState({ email: "", username: "", password: "" });
  const [creating, setCreating] = useState(false);
  const timers = useRef([]);

  useEffect(() => {
    initTheme();
    if (user) { navigate("/app", { replace: true }); return; }
    if (location.state?.url) setUrl(location.state.url);
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (raw) {
        const { brand: b, ts } = JSON.parse(raw);
        if (b && ts && Date.now() - ts < CACHE_TTL_MS) {
          setBrand(b);
          setManualFallback(!!b.manualFallback || !!b.manual);
          setUrl(b.sourceUrl || "");
          setPhase("reveal");
        } else {
          sessionStorage.removeItem(CACHE_KEY);
        }
      }
    } catch { /* ignore */ }
  }, [user, navigate]);

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  useEffect(() => () => clearTimers(), []);

  const analyze = useCallback(async () => {
    const u = url.trim();
    if (!u) { toast("Enter your website to get started", { description: "e.g. yourbrand.com" }); return; }
    setPhase("analyzing");
    setScanStep(0);
    clearTimers();
    timers.current.push(setTimeout(() => setScanStep(1), 1200));
    timers.current.push(setTimeout(() => setScanStep(2), 4500));
    timers.current.push(setTimeout(() => setScanStep(3), 9000));
    try {
      const { data } = await api.post("/public/analyze", { url: u });
      clearTimers();
      const isManual = !!data.manualFallback;
      setBrand(data);
      setManualFallback(isManual);
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ brand: data, ts: Date.now() })); } catch { /* ignore */ }
      setPhase("reveal");
      if (isManual) toast("We couldn't fully read that site", { description: "No problem — create your account and refine the details inside." });
    } catch (e) {
      clearTimers();
      setPhase("input");
      const info = limitInfo(e);
      if (e?.response?.status === 429) toast.error("You've hit the free analysis limit. Create an account to keep going.");
      else if (info) toast.error(info.message);
      else toast.error(errMsg(e, "Couldn't analyze that site. Check the URL and try again."));
    }
  }, [url]);

  const createAccount = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const data = await register({ email: form.email.trim(), username: form.username.trim(), password: form.password, brand });
      // Set the brand to auto-open BEFORE navigating, so the Studio loads it on mount.
      // (register() sets the user, which triggers the redirect effect — the id must be in place first.)
      if (data?.brandId) sessionStorage.setItem("hdg_open_brand", data.brandId);
      sessionStorage.removeItem(CACHE_KEY);
      localStorage.setItem("hdg_onboarded", "1");
      toast.success("Welcome to HDG Creative Studio 🎉");
      navigate("/app", { replace: true });
    } catch (err) {
      toast.error(errMsg(err, "Could not create your account"));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="lightboard" style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }} data-testid="onboarding-entry">
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 24px" }}>
        <button onClick={() => navigate("/")} style={{ display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", cursor: "pointer" }} data-testid="onboard-home">
          <img src="/hdg-logo.jpg" alt="HeyDesignGuy" style={{ height: 32, width: 32, objectFit: "contain", borderRadius: 7, background: "#fff", padding: 3 }} />
          <span className="font-display" style={{ fontWeight: 600, fontSize: 15, color: "var(--tprimary)" }}>HDG Creative Studio</span>
        </button>
        <button onClick={() => navigate("/login")} data-testid="onboard-login-link" className="app-link font-body" style={{ fontSize: 13, background: "none", border: "none", cursor: "pointer" }}>
          Already have an account? Log in
        </button>
      </header>

      <main style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px 20px 60px", width: "100%", maxWidth: 760, margin: "0 auto" }}>

        {phase === "input" && (
          <div style={{ width: "100%", textAlign: "center" }} className="mkt-fade-up in-view">
            <div className="eyebrow" style={{ justifyContent: "center", marginBottom: 18 }}><span>Free · No credit card</span></div>
            <h1 className="font-display" style={{ fontSize: "clamp(36px, 6vw, 56px)", fontWeight: 700, letterSpacing: "-0.03em", color: "var(--tprimary)", lineHeight: 1.05, margin: 0 }}>
              What's your website?
            </h1>
            <p className="font-body" style={{ fontSize: 18, color: "var(--tsecondary)", marginTop: 16, lineHeight: 1.5 }}>
              Paste your URL and watch your brand become a content engine — colors, voice and on-brand posts in about a minute.
            </p>
            <div style={{ display: "flex", gap: 10, marginTop: 32, maxWidth: 560, marginLeft: "auto", marginRight: "auto", flexWrap: "wrap" }}>
              <input
                data-testid="onboard-url-input"
                className="dim-input font-body"
                style={{ flex: 1, minWidth: 240, height: 56, fontSize: 16 }}
                placeholder="yourbrand.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && analyze()}
                aria-label="Your website URL"
                autoFocus
              />
              <button data-testid="onboard-getstarted-button" className="btn-primary" style={{ height: 56, padding: "0 26px", fontSize: 15 }} onClick={analyze}>
                Get Started <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {phase === "analyzing" && (
          <div style={{ width: "100%", maxWidth: 520, textAlign: "center" }} aria-live="polite">
            <div className="scan-wrap panel-card" style={{ padding: 28, position: "relative", overflow: "hidden" }}>
              <div className="scan-line" />
              <ScanLine size={34} style={{ color: "var(--orange)" }} />
              <div className="font-mono" style={{ marginTop: 16, fontSize: 12, color: "var(--tmuted)", letterSpacing: "0.06em" }}>ANALYZING</div>
              <div className="font-display" style={{ marginTop: 8, fontSize: 18, fontWeight: 600, color: "var(--tprimary)" }} data-testid="onboard-scan-status">
                {PHASES[scanStep]}
              </div>
              <div style={{ display: "flex", gap: 6, justifyContent: "center", marginTop: 18 }}>
                {PHASES.map((_, i) => (
                  <span key={i} style={{ width: i <= scanStep ? 20 : 6, height: 5, borderRadius: 999, background: i <= scanStep ? "var(--orange)" : "var(--cardhover)", transition: "all 0.4s ease" }} />
                ))}
              </div>
            </div>
            <p className="font-body" style={{ fontSize: 13, color: "var(--tmuted)", marginTop: 16 }}>Reading {url}…</p>
          </div>
        )}

        {phase === "reveal" && brand && (
          <div style={{ width: "100%" }}>
            <div className="panel-card mkt-fade-up in-view" style={{ padding: 24 }} data-testid="onboard-reveal">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <CheckCircle2 size={20} style={{ color: "var(--ok)" }} />
                <span className="font-mono" style={{ fontSize: 11, color: "var(--ok)", letterSpacing: "0.06em" }}>{manualFallback ? "READY TO REFINE" : "BRAND DNA EXTRACTED"}</span>
              </div>
              <h2 className="font-display" data-testid="onboard-brand-name" style={{ fontSize: 30, fontWeight: 700, color: "var(--tprimary)", marginTop: 12, letterSpacing: "-0.02em" }}>
                {brand.brandName || brand.siteName}
              </h2>

              {(brand.colors || []).length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 18 }}>
                  {brand.colors.slice(0, 8).map((c, i) => (
                    <div key={`${c}-${i}`} style={{ textAlign: "center" }}>
                      <div style={{ width: 46, height: 46, borderRadius: 9, background: c, border: "1px solid rgba(255,255,255,0.08)" }} />
                      <div className="font-mono" style={{ fontSize: 9, color: "var(--tmuted)", marginTop: 4 }}>{c}</div>
                    </div>
                  ))}
                </div>
              )}
              {(brand.fonts || []).length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16 }}>
                  {brand.fonts.slice(0, 4).map((f, i) => (
                    <span key={`${f}-${i}`} className="font-mono" style={{ fontSize: 11, padding: "4px 10px", borderRadius: 999, border: "1px solid var(--cardhover)", color: "var(--tsecondary)" }}>{f}</span>
                  ))}
                </div>
              )}
              {brand.voiceSummary && (
                <p className="font-body" style={{ fontSize: 14, color: "var(--tsecondary)", marginTop: 16, lineHeight: 1.6 }}>{brand.voiceSummary}</p>
              )}
            </div>

            <div className="mkt-fade-up in-view" style={{ animationDelay: "0.15s", marginTop: 26 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
                <Sparkles size={16} style={{ color: "var(--orange)" }} />
                <p className="font-display" style={{ fontSize: 16, fontWeight: 600, color: "var(--tprimary)", textAlign: "center", margin: 0 }}>
                  Looks great! Create your account to save this and start generating content.
                </p>
              </div>

              <div style={{ maxWidth: 420, margin: "20px auto 0" }}>
                <GoogleButton label="Sign up with Google" testId="onboard-google-btn" />
                <OrDivider />
              </div>
              <form onSubmit={createAccount} style={{ maxWidth: 420, margin: "0 auto", display: "flex", flexDirection: "column", gap: 12 }}>
                <input data-testid="onboard-email" className="dim-input font-body" type="email" required placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} aria-label="Email" />
                <input data-testid="onboard-username" className="dim-input font-body" required placeholder="Choose a username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} aria-label="Username" />
                <input data-testid="onboard-password" className="dim-input font-body" type="password" required placeholder="Create a password (min 8 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} aria-label="Password" />
                <button data-testid="onboard-create-button" type="submit" className="btn-primary" style={{ height: 50, fontSize: 15 }} disabled={creating}>
                  {creating ? <Loader2 size={16} className="spin" /> : <ArrowRight size={16} />}
                  {creating ? "Creating your studio..." : "Create account & continue"}
                </button>
                <button type="button" onClick={() => { setPhase("input"); setBrand(null); sessionStorage.removeItem(CACHE_KEY); }} className="font-body" style={{ background: "none", border: "none", color: "var(--tmuted)", cursor: "pointer", fontSize: 12.5 }}>
                  ← Try a different website
                </button>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
